import { useQueryClient } from '@tanstack/react-query'
import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { PositionedSidenav } from 'src/components/Sidenav/Sidenav.style'
import SidenavWrapper from 'src/components/SidenavWrapper/SidenavWrapper'
import { PusherEvents } from 'src/enums'
import { useDebounced } from 'src/hooks'
import usePusher from 'src/hooks/usePusher'
import { budgetRequestsQueryKeys } from 'src/queries/budgetRequests/budgetRequests.queries'
import { useQueryOwnUser } from 'src/queries/users.queries'
import {
  createSetIsSidenavOpen,
  createSetShowSidenavLarge,
} from 'src/redux/actions/app.actions'
import {
  selectIsSidenavLockedOpenFromState,
  selectIsSidenavOpenFromState,
} from 'src/redux/selectors/app.selectors'
import { selectFixedElementPushdown } from 'src/redux/selectors/css.selectors'

interface SidenavProps {
  className?: string
}

const Sidenav: React.FC<SidenavProps> = (): React.ReactElement => {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()

  const { data: user } = useQueryOwnUser({})
  const isSidenavOpen = useSelector(selectIsSidenavOpenFromState)
  const sideNavLockedOpen = useSelector(selectIsSidenavLockedOpenFromState)
  const fixedElementPushdown = useSelector(selectFixedElementPushdown)

  const [hovered, setHovered] = useState(false)

  const ref = useRef<HTMLDivElement>(null)

  const largeNavDivRef: React.MutableRefObject<HTMLDivElement | null> =
    useRef(null)
  const largeNavParentDivRef: React.MutableRefObject<HTMLDivElement | null> =
    useRef(null)
  const bottomNavDivRef: React.MutableRefObject<HTMLDivElement | null> =
    useRef(null)

  const [savedSwitchHeight, setSavedSwitchHeight] = useState<number | null>(
    null,
  ) // estimated value
  const [viewportHeight, setViewportHeight] = useState(window.innerHeight)

  const updateWindowSize = (): void => {
    setViewportHeight(window.innerHeight)
  }

  const { bind } = usePusher(user?.id)

  const clearance =
    (largeNavParentDivRef.current?.clientHeight ?? 0) -
    (largeNavDivRef.current?.clientHeight ?? 0)

  // TODO: move this into App.tsx with other pusher bindings
  bind(PusherEvents.BudgetRequest, () => {
    queryClient.invalidateQueries({
      queryKey: budgetRequestsQueryKeys.all(),
    })
  })

  const handleResize = (): void => {
    if (viewportHeight !== undefined) {
      if (
        bottomNavDivRef.current?.clientHeight !== undefined &&
        largeNavParentDivRef.current?.clientHeight !== undefined
      ) {
        if (clearance < 0) {
          dispatch(createSetShowSidenavLarge(false))
        }
      } else {
        if (viewportHeight > (savedSwitchHeight ?? 0)) {
          dispatch(createSetShowSidenavLarge(true))
        }
      }
    }
  }

  const { debouncedF } = useDebounced((): (() => void) => {
    window.addEventListener('resize', updateWindowSize)
    handleResize()
    return (): void => {
      window.removeEventListener('resize', updateWindowSize)
    }
  }, 100)

  useEffect(() => {
    debouncedF()
  }, [viewportHeight, clearance])

  useEffect(() => {
    if (
      bottomNavDivRef.current?.clientHeight !== undefined &&
      largeNavDivRef.current?.clientHeight !== undefined
    ) {
      setSavedSwitchHeight(
        bottomNavDivRef.current?.clientHeight +
          largeNavDivRef.current?.clientHeight,
      )
    }
  }, [
    bottomNavDivRef.current?.clientHeight,
    largeNavDivRef.current?.clientHeight,
  ])

  useEffect(() => {
    window.addEventListener('resize', updateWindowSize)
    handleResize()
    return (): void => {
      window.removeEventListener('resize', updateWindowSize)
    }
  }, [viewportHeight])

  return (
    <PositionedSidenav
      fixedElementPushdown={fixedElementPushdown}
      ref={ref}
      onMouseEnter={(): void => {
        setHovered(true)
        dispatch(createSetIsSidenavOpen(true))
      }}
      onFocus={(e): void => {
        // ignores focus from switching browser tabs
        if (e.relatedTarget !== null && !isSidenavOpen) {
          dispatch(createSetIsSidenavOpen(true))
        }
      }}
      onBlur={(e): void => {
        // only close if the focus is not on the sidenav
        if (
          !ref.current?.contains(e.relatedTarget) &&
          !sideNavLockedOpen &&
          !hovered
        ) {
          dispatch(createSetIsSidenavOpen(false))
        }
      }}
      onMouseLeave={(): void => {
        setHovered(false)
        if (!sideNavLockedOpen) {
          dispatch(createSetIsSidenavOpen(false))
        }
      }}
    >
      <SidenavWrapper fixedElementPushdown={fixedElementPushdown}/>
    </PositionedSidenav>
  )
}

export default Sidenav
